import React, { useState, useEffect, useCallback } from 'react';

const BREAKPOINTS = [
  { name: 'mobile', min: 0, max: 767, color: 'bg-rose-500', label: 'Mobile', icon: '📱' },
  { name: 'tablet', min: 768, max: 1023, color: 'bg-amber-500', label: 'Tablet', icon: '📟' },
  { name: 'desktop', min: 1024, max: Infinity, color: 'bg-emerald-500', label: 'Desktop', icon: '🖥️' },
];

function getBreakpoint(width) {
  return BREAKPOINTS?.find(bp => width >= bp?.min && width <= bp?.max) || BREAKPOINTS?.[0];
}

function getContainerWidth(width) {
  if (width >= 1280) return '1280px (xl)';
  if (width >= 1024) return '1024px (lg)';
  if (width >= 768) return '768px (md)';
  if (width >= 640) return '640px (sm)';
  return `${width}px (xs)`;
}

// --- Contrast Ratio Utilities ---

function parseColor(colorStr) {
  if (!colorStr || colorStr === 'transparent' || colorStr === 'rgba(0, 0, 0, 0)') return null;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 1;
  const ctx = canvas?.getContext('2d');
  ctx.fillStyle = colorStr;
  ctx?.fillRect(0, 0, 1, 1);
  const [r, g, b, a] = ctx?.getImageData(0, 0, 1, 1)?.data;
  if (a === 0) return null;
  return { r, g, b };
}

function linearize(c) {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

function relativeLuminance({ r, g, b }) {
  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

function contrastRatio(c1, c2) {
  const l1 = relativeLuminance(c1);
  const l2 = relativeLuminance(c2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

function getWcagLevel(ratio) {
  if (ratio >= 7) return 'AAA';
  if (ratio >= 4.5) return 'AA';
  if (ratio >= 3) return 'AA Large';
  return 'Fail';
}

function getWcagColor(level) {
  switch (level) {
    case 'AAA': return '#10b981';
    case 'AA': return '#3b82f6';
    case 'AA Large': return '#f59e0b';
    default: return '#ef4444';
  }
}

function rgbToHex({ r, g, b }) {
  return '#' + [r, g, b]?.map(v => v?.toString(16)?.padStart(2, '0'))?.join('');
}

function getEffectiveBg(el) {
  let node = el;
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    const bg = style?.backgroundColor;
    if (bg && bg !== 'transparent' && bg !== 'rgba(0, 0, 0, 0)') {
      const parsed = parseColor(bg);
      if (parsed) return parsed;
    }
    node = node?.parentElement;
  }
  // fallback to body
  const bodyBg = window.getComputedStyle(document.body)?.backgroundColor;
  return parseColor(bodyBg) || { r: 255, g: 255, b: 255 };
}

function getElementIdentifier(el) {
  const tag = el?.tagName?.toLowerCase();
  const id = el?.id ? `#${el?.id}` : '';
  const classes = Array.from(el?.classList)?.filter(c => !c?.startsWith('hover:') && !c?.startsWith('focus:'))?.slice(0, 3)?.map(c => `.${c}`)?.join('');
  return `${tag}${id}${classes}`?.slice(0, 40);
}

function scanContrastPairs() {
  const textSelectors = 'p, h1, h2, h3, h4, h5, h6, span, a, button, label, li, td, th, div, small, strong, em, b, i';
  const elements = Array.from(document.querySelectorAll(textSelectors));
  const results = [];
  const seen = new Set();

  for (const el of elements) {
    // Skip hidden or empty elements
    const rect = el?.getBoundingClientRect();
    if (rect?.width === 0 || rect?.height === 0) continue;
    if (rect?.bottom < 0 || rect?.top > window.innerHeight) continue;

    const text = el?.textContent?.trim();
    if (!text || text?.length === 0) continue;

    // Only process leaf-ish text nodes (avoid duplicating parent/child)
    const directText = Array.from(el?.childNodes)?.some(n => n?.nodeType === 3 && n?.textContent?.trim());
    if (!directText) continue;

    const style = window.getComputedStyle(el);
    const fgColor = parseColor(style?.color);
    if (!fgColor) continue;

    const bgColor = getEffectiveBg(el);
    const fgHex = rgbToHex(fgColor);
    const bgHex = rgbToHex(bgColor);
    const key = `${fgHex}|${bgHex}`;
    if (seen?.has(key)) continue;
    seen?.add(key);

    const ratio = contrastRatio(fgColor, bgColor);
    const level = getWcagLevel(ratio);
    const identifier = getElementIdentifier(el);

    results?.push({ fgHex, bgHex, ratio, level, identifier });
  }

  // Sort by ratio descending
  return results?.sort((a, b) => b?.ratio - a?.ratio);
}

const LEVEL_ORDER = ['AAA', 'AA', 'AA Large', 'Fail'];

export default function ViewportIndicator() {
  const [viewport, setViewport] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('viewport'); // 'viewport' | 'contrast'
  const [contrastResults, setContrastResults] = useState([]);
  const [isScanning, setIsScanning] = useState(false);

  // Only render in development mode
  if (import.meta.env?.MODE !== 'development') return null;

  useEffect(() => {
    const handleResize = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const runContrastScan = useCallback(() => {
    setIsScanning(true);
    setTimeout(() => {
      const results = scanContrastPairs();
      setContrastResults(results);
      setIsScanning(false);
    }, 50);
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'contrast' && contrastResults?.length === 0) {
      runContrastScan();
    }
  };

  const bp = getBreakpoint(viewport?.width);
  const containerWidth = getContainerWidth(viewport?.width);

  const tailwindBreakpoints = [
    { label: 'sm', px: 640, active: viewport?.width >= 640 },
    { label: 'md', px: 768, active: viewport?.width >= 768 },
    { label: 'lg', px: 1024, active: viewport?.width >= 1024 },
    { label: 'xl', px: 1280, active: viewport?.width >= 1280 },
    { label: '2xl', px: 1536, active: viewport?.width >= 1536 },
  ];

  // Group contrast results by WCAG level
  const grouped = LEVEL_ORDER?.reduce((acc, level) => {
    acc[level] = contrastResults?.filter(r => r?.level === level);
    return acc;
  }, {});

  return (
    <div
      className="fixed bottom-4 left-4 z-[9999] font-mono select-none"
      style={{ userSelect: 'none' }}
    >
      {/* Collapsed pill */}
      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-xs font-bold shadow-lg ${bp?.color} opacity-80 hover:opacity-100 transition-opacity`}
          title="Click to expand viewport info"
        >
          <span>{bp?.icon}</span>
          <span>{bp?.label}</span>
          <span className="opacity-75">{viewport?.width}px</span>
        </button>
      )}
      {/* Expanded panel */}
      {isExpanded && (
        <div className="bg-gray-900 text-white rounded-xl shadow-2xl border border-gray-700 overflow-hidden w-72">
          {/* Header */}
          <div className={`flex items-center justify-between px-3 py-2 ${bp?.color}`}>
            <div className="flex items-center gap-2">
              <span className="text-base">{bp?.icon}</span>
              <span className="text-sm font-bold">Dev Tools</span>
            </div>
            <button
              onClick={() => setIsExpanded(false)}
              className="text-white opacity-75 hover:opacity-100 text-lg leading-none"
              title="Collapse"
            >
              ×
            </button>
          </div>

          {/* Tab bar */}
          <div className="flex border-b border-gray-700">
            <button
              onClick={() => handleTabChange('viewport')}
              className={`flex-1 px-3 py-1.5 text-xs font-semibold transition-colors ${
                activeTab === 'viewport' ?'bg-gray-800 text-white border-b-2 border-teal-400' :'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
              }`}
            >
              📐 Viewport
            </button>
            <button
              onClick={() => handleTabChange('contrast')}
              className={`flex-1 px-3 py-1.5 text-xs font-semibold transition-colors ${
                activeTab === 'contrast' ?'bg-gray-800 text-white border-b-2 border-purple-400' :'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
              }`}
            >
              🎨 Contrast
            </button>
          </div>

          {/* VIEWPORT TAB */}
          {activeTab === 'viewport' && (
            <div>
              {/* Viewport dimensions */}
              <div className="px-3 py-2 border-b border-gray-700">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-xs">Viewport</span>
                  <span className="text-xs font-semibold text-white">{viewport?.width} × {viewport?.height}px</span>
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-gray-400 text-xs">Container</span>
                  <span className="text-xs font-semibold text-teal-400">{containerWidth}</span>
                </div>
              </div>

              {/* Tailwind breakpoints */}
              <div className="px-3 py-2 border-b border-gray-700">
                <p className="text-gray-400 text-xs mb-2 uppercase tracking-wide">Tailwind Breakpoints</p>
                <div className="flex flex-wrap gap-1.5">
                  {tailwindBreakpoints?.map(b => (
                    <span
                      key={b?.label}
                      className={`px-2 py-0.5 rounded text-xs font-bold ${
                        b?.active
                          ? 'bg-teal-600 text-white' :'bg-gray-700 text-gray-500'
                      }`}
                    >
                      {b?.label}
                    </span>
                  ))}
                </div>
              </div>

              {/* Range info */}
              <div className="px-3 py-2 border-b border-gray-700">
                <p className="text-gray-400 text-xs mb-1 uppercase tracking-wide">Active Range</p>
                <p className="text-xs text-white">
                  {bp?.max === Infinity
                    ? `≥ ${bp?.min}px`
                    : `${bp?.min}px – ${bp?.max}px`}
                </p>
              </div>

              {/* Min touch target reminder */}
              <div className="px-3 py-2">
                <p className="text-gray-400 text-xs mb-1 uppercase tracking-wide">A11y Touch Target</p>
                <p className="text-xs">
                  <span className={bp?.name === 'mobile' ? 'text-amber-400 font-bold' : 'text-gray-400'}>
                    Min 44px on mobile
                  </span>
                </p>
              </div>
            </div>
          )}

          {/* CONTRAST TAB */}
          {activeTab === 'contrast' && (
            <div>
              {/* Scan controls */}
              <div className="px-3 py-2 border-b border-gray-700 flex items-center justify-between">
                <span className="text-gray-400 text-xs">
                  {contrastResults?.length > 0
                    ? `${contrastResults?.length} unique pairs`
                    : 'Scan visible text'}
                </span>
                <button
                  onClick={runContrastScan}
                  disabled={isScanning}
                  className="text-xs px-2 py-1 rounded bg-purple-700 hover:bg-purple-600 text-white font-semibold disabled:opacity-50 transition-colors"
                >
                  {isScanning ? '⏳ Scanning…' : '🔍 Re-scan'}
                </button>
              </div>

              {/* Results */}
              <div className="overflow-y-auto" style={{ maxHeight: '320px' }}>
                {isScanning && (
                  <div className="px-3 py-6 text-center text-gray-400 text-xs">Scanning…</div>
                )}

                {!isScanning && contrastResults?.length === 0 && (
                  <div className="px-3 py-6 text-center text-gray-400 text-xs">No text elements found</div>
                )}

                {!isScanning && LEVEL_ORDER?.map(level => {
                  const items = grouped?.[level];
                  if (!items?.length) return null;
                  const levelColor = getWcagColor(level);
                  return (
                    <div key={level}>
                      {/* Level header */}
                      <div
                        className="px-3 py-1 flex items-center gap-2 sticky top-0"
                        style={{ backgroundColor: '#1f2937' }}
                      >
                        <span
                          className="text-xs font-bold px-1.5 py-0.5 rounded"
                          style={{ backgroundColor: levelColor, color: '#fff' }}
                        >
                          {level}
                        </span>
                        <span className="text-gray-400 text-xs">
                          {level === 'AAA' && '≥7:1'}
                          {level === 'AA' && '≥4.5:1'}
                          {level === 'AA Large' && '≥3:1'}
                          {level === 'Fail' && '<3:1'}
                        </span>
                        <span className="ml-auto text-gray-500 text-xs">{items?.length}</span>
                      </div>
                      {/* Items */}
                      {items?.map((item, idx) => (
                        <div
                          key={idx}
                          className="px-3 py-1.5 border-b border-gray-800 flex items-center gap-2"
                        >
                          {/* Color swatches */}
                          <div className="flex items-center gap-1 shrink-0">
                            <span
                              className="inline-block w-4 h-4 rounded-sm border border-gray-600"
                              style={{ backgroundColor: item?.fgHex }}
                              title={`FG: ${item?.fgHex}`}
                            />
                            <span className="text-gray-600 text-xs">/</span>
                            <span
                              className="inline-block w-4 h-4 rounded-sm border border-gray-600"
                              style={{ backgroundColor: item?.bgHex }}
                              title={`BG: ${item?.bgHex}`}
                            />
                          </div>

                          {/* Identifier */}
                          <span
                            className="text-gray-300 text-xs truncate flex-1"
                            title={item?.identifier}
                          >
                            {item?.identifier}
                          </span>

                          {/* Ratio */}
                          <span
                            className="text-xs font-bold shrink-0"
                            style={{ color: levelColor }}
                          >
                            {item?.ratio?.toFixed(2)}:1
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              {!isScanning && contrastResults?.length > 0 && (
                <div className="px-3 py-2 border-t border-gray-700 flex flex-wrap gap-2">
                  {LEVEL_ORDER?.map(level => (
                    <span key={level} className="flex items-center gap-1">
                      <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ backgroundColor: getWcagColor(level) }}
                      />
                      <span className="text-gray-400 text-xs">{level}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
